import React, { useEffect, useState } from 'react';
import type { Venue } from '../../../types/venue';
import { fetchVenueAnalytics } from '../../../utils/venueApi';

interface OverviewPanelProps {
  venue: Venue;
}

interface AnalyticsData {
  totalGuests: number;
  visitsToday: number;
  activePunchCards: number;
  pendingRedemptions: number;
}

const OverviewPanel: React.FC<OverviewPanelProps> = ({ venue }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVenueAnalytics(venue.id)
      .then((data) => setAnalytics(data))
      .catch(() => setAnalytics({ totalGuests: 0, visitsToday: 0, activePunchCards: 0, pendingRedemptions: 0 }))
      .finally(() => setLoading(false));
  }, [venue.id]);

  const enabledFeatures = Object.entries(venue.features).filter(([, v]) => v).length;
  const trialEnd = new Date(venue.trialStart);
  trialEnd.setDate(trialEnd.getDate() + 30);
  const trialDaysLeft = Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));

  const stats = [
    {
      label: 'Active features',
      value: String(enabledFeatures),
    },
    {
      label: 'Trial status',
      value: venue.subscriptionStatus === 'trial' ? `${trialDaysLeft}d left` : venue.subscriptionStatus,
    },
    {
      label: 'Total guests',
      value: loading ? '—' : String(analytics?.totalGuests ?? 0),
    },
    {
      label: "Today's visits",
      value: loading ? '—' : String(analytics?.visitsToday ?? 0),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Overview</div>
        <h2 className="text-display text-2xl text-coffee-900">At a glance</h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white border border-coffee-100 rounded-xl p-4 hover-lift"
          >
            <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-1">{stat.label}</p>
            <p className={`text-2xl font-mono text-coffee-900 ${loading && stat.label.includes('guest') ? 'animate-pulse' : ''}`}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-coffee-50 border border-coffee-100 rounded-xl p-4">
        <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-1.5">Venue URL</p>
        <p className="font-mono text-sm text-coffee-700 select-all">
          https://whobrew.us/v/{venue.slug}
        </p>
      </div>
    </div>
  );
};

export default OverviewPanel;
